import { Component, ElementRef, Inject, OnInit, QueryList, ViewChildren, ViewEncapsulation } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subject, finalize, takeUntil } from 'rxjs';
import { FileInputValue } from '@ngx-dropzone/cdk';
import { AppConfigService } from 'src/app/app-config.service';
import { AuthService } from 'src/app/auth/auth.service';
import { EncryptionService } from 'src/app/auth/encryption.service';
import { CommonService } from 'src/app/common.service';
import { GuestLoginService } from 'src/app/guest-login.service';
import { UserService } from 'src/app/services/user.service';

@Component({
  selector: 'app-login-popup',
  templateUrl: './login-popup.component.html',
  styleUrls: ['./login-popup.component.less'],
  encapsulation: ViewEncapsulation.None
})
export class LoginPopupComponent implements OnInit {
  @ViewChildren('otpInput') otpInputs!: QueryList<ElementRef>;

  userEmailId: string = '';
  userPassword: string = '';
  emailControl = new FormControl('', [Validators.required, Validators.email]);
  validEmail: any;
  hide: boolean = true;
  hideNewPassword: boolean = true;
  hideConfirmPassword: boolean = true;
  invalidUserCredentials: boolean = false;
  currentUser: any = {};
  isChangePassword = false;
  isOtpSend = false;
  isotpSumitted = false;
  isPasswordConfirmed = false;
  isResetPassword: boolean = false;
  appConfig: any;
  isMailsend: boolean = false;
  isForgetPasswordEnabled: boolean = false;
  isCodeVerified: boolean = false;
  resetUser: any;
  hideOldPassword: boolean = true;
  destroy$: Subject<boolean> = new Subject<boolean>();
  invalidNewUserPassword: boolean = false;
  passwordConstraintMsg: any;
  licenseInfo: any = null;
  licenseDueDays: number = 0;
  licenseSeverityClass: string = '';
  showLicenseAlert: boolean = false;
  forgetPasswordObj: any = {
    emailId: '',
    verificationCode: '',
    oldPassword: '',
    newPassword: '',
    confirmedPassword: ''
  };

  resetPassword: any = {
    emailId: '',
    newPassword: '',
    confirmedPassword: ''
  }
  showLoader: boolean = false;
  userEmails = new FormGroup({
    primaryEmail: new FormControl('', [
      Validators.required,
      Validators.pattern("^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$")]
    ),
  });

  rolesDispText: any = {
    "SUPER_ADMIN": "Super Admin",
    "APPLICATION_ADMIN": "Application Admin",
    "TECHNICIAN": "Technician"
  }

  renewLicense: boolean = false;
  isLicenseUploading: boolean = false;
  filesCtrl = new FormControl<FileInputValue>(null, Validators.required);

  isMFA: boolean = false;
  qrImageBase64: string = '';
  encodedSecret: string = '';
  otp: string[] = ['', '', '', '', '', ''];
  get qrImageSrc(): string {
    if (this.qrImageBase64.startsWith('data:image')) {
      return this.qrImageBase64;
    }
    return `data:image/png;base64,${this.qrImageBase64}`;
  }
  get otpValue(): string {
    return this.otp.join('');
  }
  get isComplete(): boolean {
    return this.otp.every(v => v !== '');
  }
  get selectedLicenseFileName(): string {
    const value = this.filesCtrl.value;
    if (Array.isArray(value)) {
      return value.map(file => file.name).join(', ');
    }
    return value?.name || '';
  }

  constructor(public dialogRef: MatDialogRef<LoginPopupComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly authService: AuthService,
    private readonly userService: UserService,
    private router: Router,
    public commonService: CommonService,
    private readonly configService: AppConfigService,
    private readonly encrpytService: EncryptionService,
    private readonly activatedRoute: ActivatedRoute,
    public translate: TranslateService,
    private readonly guestLoginService: GuestLoginService) {
    this.translate.addLangs(['en']);
    this.translate.setDefaultLang('en');
    this.translate.use('en');
    this.appConfig = this.configService.config;
  }

  ngOnInit(): void {
    this.activatedRoute.queryParams?.subscribe(params => {
      let paramsKey: any = Object.keys(params);
      if (paramsKey.length > 0) {
        this.resetPassword.emailId = atob(paramsKey[0]);
        this.isResetPassword = true;
      } else {
        this.isResetPassword = false;
      }
    });
    if (this.commonService.ProjectLSData?.['isGuestLogin']) {
      delete this.commonService.ProjectLSData?.['isGuestLogin'];
      this.commonService.setProjectLSData();
    }
    this.isChangePassword = this.data.changePassword ? this.data.changePassword : false;
    this.renewLicense = this.data.renewLicense ? this.data.renewLicense : false;
    this.passwordConstraintMsg = (this.isChangePassword) ? this.translate.instant("USER_CONFIGURATION.Password_Constraint_Message") : '';
    this.isOtpSend = false;
    this.isotpSumitted = false;
    this.isPasswordConfirmed = false;
    this.currentUser = this.commonService.ProjectLSData?.currentUser;
    if (this.data.isAccessTokenExpired || this.isChangePassword || this.isResetPassword) {
      this.userEmailId = this.currentUser?.email;
    }
    this.setLicenseInfo();
  }

  setLicenseInfo() {
    this.licenseInfo = this.commonService.ProjectLSData?.currentLicenseStatus || null;
    const dueInfo = this.commonService.getLicenseDueInfo(this.licenseInfo);
    this.licenseDueDays = dueInfo.dueDays;
    this.licenseSeverityClass = dueInfo.severityClass;
    this.showLicenseAlert = dueInfo.shouldShow;
  }

  showLicenseDetails() {
    if (!this.licenseInfo) {
      return;
    }

    this.renewLicense = true;
  }

  clearSelectedLicenseFile() {
    this.filesCtrl.setValue(null);
  }

  private getSelectedLicenseFile(): File | null {
    const fileInput = this.filesCtrl.value;
    return (Array.isArray(fileInput) ? fileInput[0] : fileInput) || null;
  }

  validateLogin() {
    this.invalidUserCredentials = false;
    let userCredentials = {
      "email": this.userEmailId,
      "password": this.userPassword
    };
    this.authService.login(userCredentials).pipe(takeUntil(this.destroy$)).subscribe((res: any) => {
      if (res == "true") {
        if (!this.appConfig.mfaMendatory) {
          this.getUserInfo();
        } else {
          this.isMFA = this.authService.isMFARegistered();
          if (!this.isMFA) {
            this.authService.getTOTP().subscribe((res: any) => {
              this.encodedSecret = res.data.encodedSecret;
              this.qrImageBase64 = res.data.qrCode;
            });
          }
        }
      } else if (res == "resetPassword") {
        this.resetPassword.emailId = this.userEmailId;
        this.isResetPassword = true;
      }
      else {
        this.invalidUserCredentials = true;
      }
    },
    error => {
      if (!error.ok && error.status === 403 && error.statusText === 'Forbidden' && this.commonService?.ProjectLSData?.currentLicenseStatus?.status === 'EXPIRED') {
        this.commonService.showToastMsg('License expired! Please renew your license.', 'error');
      }
    });
  }

  onOTPInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    const val = input.value.replace(/[^0-9]/g, '');
    this.otp[index] = val;
    input.value = val;
    if (val && index < 5) {
      this.otpInputs.toArray()[index + 1].nativeElement.focus();
    }
  }

  keydownOnOTP(event: KeyboardEvent, index: number): void {
    if (event.key === 'Backspace' && !this.otp[index] && index > 0) {
      this.otp[index - 1] = '';
      this.otpInputs.toArray()[index - 1].nativeElement.focus();
    } else if (event.key === 'Enter' && this.isComplete) {
      this.validateOtp();
    }
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const paste = event.clipboardData?.getData('text').replace(/[^0-9]/g, '').slice(0, 6) || '';
    paste.split('').forEach((char, i) => { this.otp[i] = char; });
    const inputs = this.otpInputs.toArray();
    if (inputs[paste.length - 1]) inputs[paste.length - 1].nativeElement.focus();
  }

  trackByIndex(index: number): number {
    return index;
  }

  validateOtp() {
    if (this.isMFA) {
      this.authService.verifyOTP(this.otpValue).pipe(takeUntil(this.destroy$)).subscribe((res: any) => {
        if (res.success) {
          this.getUserInfo();
        } else {
          this.commonService.showToastMsg('Error validating OTP. Please try again.', 'error');
        }
      }, (err) => {
        console.error('Error validating OTP', err);
        this.commonService.showToastMsg('Error validating OTP. Please try again.', 'error');
      });
    } else {
      this.authService.registerTOTP(this.encodedSecret, this.otpValue).pipe(takeUntil(this.destroy$)).subscribe((res: any) => {
        if (res.success) {
          this.getUserInfo();
        } else {
          this.commonService.showToastMsg('Error validating OTP. Please try again.', 'error');
        }
      }, (err) => {
        console.error('Error validating OTP', err);
        this.commonService.showToastMsg('Error validating OTP. Please try again.', 'error');
      });
    }
  }

  getUserInfo() {
    this.userService.getUserInfo().pipe(takeUntil(this.destroy$)).subscribe((res: any) => {
      if (res) {
        if (this.data.isAccessTokenExpired) {
          this.dialogRef.close({ loginStatus: "success" });
        } else {
          this.commonService.ProjectLSData["currentUser"] = res;
          this.commonService.setProjectLSData();
          this.authService.storeCurrentUser(this.commonService.ProjectLSData?.currentUser);
          this.userService.getUserRoles().subscribe((res: any) => {
            if (res != "") {
              let roleArr = [];
              for (let i = 0; i < res.length; i++) {
                roleArr.push({
                  role: res[i],
                  roleDisplayText: this.rolesDispText[res[i]] ?? 'Technician'
                });
              }
              this.commonService.ProjectLSData["userRoleList"] = roleArr;
              this.commonService.setProjectLSData();
              let landingPageInfo = this.commonService.getUserRoleLandingPage(roleArr[0]?.role);
              this.router.navigate([`/mainboard/${landingPageInfo.landingPage}`]);
              this.dialogRef.close();
            } else {
              this.authService.logout();
            }
          });
          this.dialogRef.close({ loginStatus: 'success' });
        }
      }
    });
  }

  newUserPasswordReset() {
    this.userEmailId = '';
    this.userPassword = '';
    let userData = {
      email: this.resetPassword.emailId,
      password: this.resetPassword.confirmedPassword
    }
    this.authService.resetNewUserPassword(userData).subscribe((response: any) => {
      if (response.success) {
        this.isResetPassword = false;
        this.resetPassword.emailId = '';
        this.resetPassword.newPassword = '';
        this.resetPassword.confirmedPassword = '';
        this.commonService.showToastMsg(response.message, 'success');
        this.router.navigate(['/login']);
      } else {
        this.commonService.showToastMsg(response.message, 'error');
      }
    })
  }

  closeResetPassword() {
    this.isResetPassword = false;
  }

  forgetPassword() {
    this.isChangePassword = true;
  }

  validateEmail() {
    this.isOtpSend = true;
  }

  submitOtp() {
    this.isotpSumitted = true;
  }

  submitPassword() {
    this.isPasswordConfirmed = true;
  }

  reloadOnInit() {
    this.ngOnInit();
  }

  CancelLogin() {
    this.dialogRef.close();
    this.router.navigate(['']);
  }

  enableForgetPassword() {
    this.isForgetPasswordEnabled = true;
  }

  sendMail() {
    this.showLoader = true;
    if (this.userEmails.get('primaryEmail')?.invalid && this.userEmails.get('primaryEmail')?.touched && this.forgetPasswordObj.emailId)
      this.commonService.sendMail(this.forgetPasswordObj.emailId).pipe(finalize(() => this.showLoader = false)).subscribe((res: any) => {
        this.isMailsend = true;
      }, (error: any) => {
        this.showLoader = false;
      });
  }

  verifyCode() {
    this.commonService.verifyCode({
      userId: this.forgetPasswordObj.emailId,
      verificationCode: this.forgetPasswordObj.verificationCode
    }).subscribe((res: any) => {
      if (res.success)
        this.isCodeVerified = true;
    })
  }

  validatePasswordBlur() {
    if (!this.validatePassword(this.forgetPasswordObj.newPassword)) {
      this.invalidNewUserPassword = true;
      return;
    }
    else this.invalidNewUserPassword = false;
  }

  validatePassword(password: String) {
    return String(password)
      .match(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{7,}$/
      );
  };

  setNewPassword() {
    let resetPasswordSevice = this.isChangePassword ? this.commonService.changePassword({
      oldPassword: this.forgetPasswordObj.oldPassword,
      newPassword: this.forgetPasswordObj.newPassword
    }) : this.commonService.resetPassword({
      email: this.forgetPasswordObj.emailId,
      password: this.forgetPasswordObj.newPassword
    });
    if (this.forgetPasswordObj.newPassword == this.forgetPasswordObj.confirmedPassword) {
      if (this.forgetPasswordObj.newPassword.length < 8) {
        this.commonService.showToastMsg(this.translate.instant("USER_CONFIGURATION.Min_Password_Length"), 'warn');
        return;
      };
      if (this.forgetPasswordObj.newPassword.length > 16) {
        this.commonService.showToastMsg(this.translate.instant("USER_CONFIGURATION.Max_Password_Length"), 'warn');
        return;
      }
      resetPasswordSevice.subscribe((res: any) => {
        if (res.success) {
          this.isForgetPasswordEnabled = this.isMailsend = this.isCodeVerified = false;
          this.commonService.showToastMsg(res.message, 'success');
          if (this.isChangePassword) {
            this.dialogRef.close();
          } else {
            this.closeForgetPassword();
          }
          this.forgetPasswordObj = {
            emailId: '',
            verificationCode: '',
            oldPassword: '',
            newPassword: '',
            confirmedPassword: ''
          }
        }
      }, (err: any) => {
      }
      )
    } else {
      this.commonService.showToastMsg(this.translate.instant("USER_CONFIGURATION.New_Password_Match_Error"), 'warn');
    }

  }

  closeForgetPassword() {
    this.isForgetPasswordEnabled = this.isMailsend = this.isCodeVerified = false;
    this.forgetPasswordObj = {
      emailId: '',
      verificationCode: '',
      newPassword: '',
      confirmedPassword: ''
    };
    this.userEmails.reset();
  }

  CancelChangePassword() {
    this.forgetPasswordObj = {
      emailId: '',
      verificationCode: '',
      newPassword: '',
      confirmedPassword: ''
    };
    this.dialogRef.close();
  }

  checkEmail() {
    if (this.emailControl.valid) {
      this.validEmail = true;
    } else {
      this.validEmail = false;
    }
  }


  enableGuestLogin() {
    this.commonService.ProjectLSData['isGuestLogin'] = true;
    this.commonService.ProjectLSData['useAccessToken'] = this.appConfig.ssoConfig.useAccessToken;
    this.commonService.setProjectLSData();
    this.guestLoginService.enableGuestLogin();
  }

  logout() {
    this.guestLoginService.logout();
  }

  cancelUpload(event: MouseEvent) {
    if (this.data.location && this.data.location == 'header') {
      this.clearSelectedLicenseFile();
      this.filesCtrl.markAsUntouched();
      this.dialogRef.close();
      event.stopPropagation();
      return;
    }

    this.renewLicense = false;
    this.clearSelectedLicenseFile();
    this.filesCtrl.markAsUntouched();
    event.stopPropagation();
  }

  uploadLicense() {
    this.filesCtrl.markAsTouched();
    const file = this.getSelectedLicenseFile();

    if (!file) {
      this.commonService.showToastMsg('Please select a license file to upload.', 'warn');
      return;
    }

    if (!file.name?.toLowerCase().endsWith('.lic')) {
      this.commonService.showToastMsg('Please select a valid .lic file.', 'warn');
      return;
    }

    this.isLicenseUploading = true;
    this.commonService.uploadLicenseFile(file)
      .pipe(finalize(() => this.isLicenseUploading = false), takeUntil(this.destroy$))
      .subscribe((response: string) => {
        this.commonService.showToastMsg(response || 'License uploaded successfully.', 'success');
        this.clearSelectedLicenseFile();
        this.filesCtrl.markAsUntouched();
        this.renewLicense = false;

        this.commonService.getLicenseStatus().pipe(takeUntil(this.destroy$)).subscribe((status: any) => {
          this.commonService.setCurrentLicenseStatus(status);
          this.licenseInfo = status;
          this.setLicenseInfo();
        });

        if (this.data.location && this.data.location == 'header') {
          this.dialogRef.close({ licenseUploadStatus: 'success' });
        }
      }, (error: any) => {
         this.clearSelectedLicenseFile();
        this.filesCtrl.markAsUntouched();
        this.commonService.showToastMsg(error?.error|| error?.error?.text || error?.error?.message || 'Unable to upload the license file.', 'error');
      });
  }
}


